/** Display formatting. Pure functions, no React. */

/**
 * Shown where a value is absent.
 *
 * A single constant rather than a literal repeated at each call site, so the
 * placeholder stays identical across tables, tiles and the detail drawer.
 */
export const EMPTY = '-'

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const numberFormatter = new Intl.NumberFormat('en-US')

export function formatNumber(value: number | null | undefined): string {
  return value == null ? EMPTY : numberFormatter.format(value)
}

/**
 * Money from the API's decimal string.
 *
 * The backend sends `amountValue` as a string to avoid float rounding; parse
 * only for display and fall back to the portal's own text when absent.
 */
export function formatMoney(value: string | null | undefined): string {
  if (value == null || value === '') return EMPTY
  const parsed = Number(value)
  return Number.isFinite(parsed) ? moneyFormatter.format(parsed) : value
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY
  // Timestamps arrive as naive UTC from Postgres; mark them so they are not
  // reinterpreted as local time.
  const normalised = /[Z+]|-\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`
  const parsed = new Date(normalised)
  if (Number.isNaN(parsed.getTime())) return EMPTY
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return EMPTY
  const normalised = /[Z+]|-\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`
  const then = new Date(normalised).getTime()
  if (Number.isNaN(then)) return EMPTY

  const seconds = Math.round((Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return EMPTY
  if (seconds < 1) return '<1s'
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds % 60)}s`
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** Join a name list for a table cell, noting any overflow. */
export function formatNames(names: string[], limit = 2): string {
  if (names.length === 0) return EMPTY
  if (names.length <= limit) return names.join('; ')
  return `${names.slice(0, limit).join('; ')} +${names.length - limit} more`
}

export function titleCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/\b[a-z]/g, (character) => character.toUpperCase())
}
